import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  COMMON_TIMEZONES,
  getUserTimezone,
  setUserTimezone,
  detectUserTimezone,
  getTimezoneInfo,
} from '../../utils/timezoneUtils';

interface TimezoneSelectorProps {
  onChange?: (timezone: string) => void;
  className?: string;
}

const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({ onChange, className = '' }) => {
  const { t, i18n } = useTranslation();
  const [selectedTimezone, setSelectedTimezone] = useState<string>(getUserTimezone());
  const [timezoneInfo, setTimezoneInfo] = useState(getTimezoneInfo());

  useEffect(() => {
    // Update timezone info when selection changes
    setTimezoneInfo(getTimezoneInfo(selectedTimezone));
  }, [selectedTimezone]);

  const handleTimezoneChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newTimezone = e.target.value;
    setSelectedTimezone(newTimezone);
    setUserTimezone(newTimezone);

    if (onChange) {
      onChange(newTimezone);
    }

    // Trigger a page reload to update all timestamps
    window.location.reload();
  };

  const handleAutoDetect = () => {
    const detected = detectUserTimezone();
    setSelectedTimezone(detected);
    setUserTimezone(detected);

    if (onChange) {
      onChange(detected);
    }

    // Trigger a page reload to update all timestamps
    window.location.reload();
  };

  const isArabic = i18n.language === 'ar';

  return (
    <div className={`space-y-3 ${className}`}>
      <div>
        <label
          htmlFor="timezone-select"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          {t('profile.timezone.label', 'Timezone')}
        </label>
        <select
          id="timezone-select"
          value={selectedTimezone}
          onChange={handleTimezoneChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {COMMON_TIMEZONES.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {isArabic ? tz.labelAr : tz.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          <p>
            {t('profile.timezone.current', 'Current timezone')}:{' '}
            <span className="font-medium text-gray-900">{timezoneInfo.timezone}</span>
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {t('profile.timezone.offset', 'UTC offset')}: {timezoneInfo.offset} (
            {timezoneInfo.abbreviation})
          </p>
        </div>

        <button
          type="button"
          onClick={handleAutoDetect}
          className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
        >
          {t('profile.timezone.autoDetect', 'Auto-detect')}
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-start rtl:space-x-reverse">
          <svg
            className="w-5 h-5 text-blue-600 mt-0.5 ltr:mr-3 rtl:ml-3 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-medium">
              {t('profile.timezone.infoTitle', 'About timezone settings')}
            </p>
            <p className="mt-1 text-blue-700">
              {t(
                'profile.timezone.infoText',
                'All dates and times across the website will be displayed in your selected timezone. This helps you see accurate timestamps for your requests, tickets, and history.'
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimezoneSelector;
